import {useMemo, useState, type ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {AlertCircle, Bell, ChevronDown, MonitorX} from 'lucide-react';
import {VideoTrack, isTrackReference, useMediaDevices, useTracks} from '@livekit/components-react';
import {Track} from 'livekit-client';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {deviceKindKey, loadDevicePreferences, saveDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {supportsAudioOutputSelection, toDeviceOptions} from '../../groupCalls/services/mediaDevices';
import type {MediaDevicePreferences} from '../../groupCalls/platform/WebRtcPlatform';
import {CallDialog, DialogButton} from '../components/CallDialog';
import {groupActions} from './groupActions';
import {groupSurfaceMessages as m} from './groupMessages';

export const ShareStartDialog = ({onCancel, onContinue}: {onCancel: () => void; onContinue: () => void}) => {
    const intl = useIntl();
    return (
        <CallDialog
            title={intl.formatMessage(m.shareStartTitle)}
            testId="group-call-share-start"
            onClose={onCancel}
            footer={(
                <>
                    <DialogButton tone="neutral" label={intl.formatMessage(m.cancel)} onClick={onCancel}/>
                    <DialogButton tone="brand" label={intl.formatMessage(m.continue)} testId="group-call-share-continue" onClick={onContinue}/>
                </>
            )}
        >
            <p className="text-sm leading-[1.45] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.shareStartBody)}</p>
            <div className="flex gap-3 rounded-xl bg-[var(--phq-call-warning-pill)] px-4 py-3 text-sm leading-[1.4] text-[var(--phq-call-warning-ink)]">
                <Bell size={18} className="mt-0.5 shrink-0" aria-hidden="true"/>
                <p>
                    <strong className="font-semibold">{intl.formatMessage(m.shareStartNoteLead)}</strong> {intl.formatMessage(m.shareStartNoteBody)}
                </p>
            </div>
        </CallDialog>
    );
};

export const HowToAllowDialog = ({kind, onClose}: {kind: 'microphone' | 'camera'; onClose: () => void}) => {
    const intl = useIntl();
    return (
        <CallDialog
            role="alertdialog"
            width="sm"
            title={intl.formatMessage(kind === 'microphone' ? m.micBlockedLead : m.cameraBlockedLead)}
            onClose={onClose}
            footer={<DialogButton tone="brand" label={intl.formatMessage(m.close)} onClick={onClose}/>}
        >
            <p className="text-sm leading-[1.45] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.howToAllowBody)}</p>
        </CallDialog>
    );
};

export const DeviceSelect = ({kind, label, value, onChange}: {kind: MediaDeviceKind; label: string; value?: string; onChange: (deviceId: string) => void}) => {
    const intl = useIntl();
    const devices = useMediaDevices({kind});
    const options = useMemo(() => toDeviceOptions(devices, label), [devices, label]);
    return (
        <label className="flex flex-col gap-2">
            <span className="text-sm text-[var(--phq-call-ink-muted)]">{label}</span>
            <span className="relative">
                <select
                    data-testid={`group-call-device-${kind}`}
                    value={value ?? ''}
                    onChange={event => onChange(event.target.value)}
                    className="h-11 w-full appearance-none rounded-[10px] bg-[var(--phq-call-surface-muted)] pl-4 pr-10 text-[15px] font-medium text-[var(--phq-call-ink)] shadow-[inset_0_0_0_1px_var(--phq-call-line-strong)] outline-none focus:shadow-[inset_0_0_0_2px_var(--phq-call-brand-focus)]"
                >
                    <option value="">{intl.formatMessage(gm.systemDefault)}</option>
                    {options.map(option => (
                        <option key={option.deviceId} value={option.deviceId}>{option.label}</option>
                    ))}
                </select>
                <ChevronDown size={20} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
            </span>
        </label>
    );
};

export const MicBlockedNote = () => {
    const intl = useIntl();
    return (
        <div role="alert" data-testid="devices-mic-blocked" className="rounded-xl bg-[var(--phq-call-danger-tile)] px-3 py-2.5 text-[13px] leading-[1.45]">
            <p className="font-semibold text-[var(--phq-call-danger-soft)]">{intl.formatMessage(m.devicesMicBlockedTitle)}</p>
            <p className="mt-0.5 text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.devicesMicBlockedBody)}</p>
        </div>
    );
};

export const CameraPreview = () => {
    const intl = useIntl();
    const local = useTracks([{source: Track.Source.Camera, withPlaceholder: false}], {onlySubscribed: false})
        .find(trackRef => trackRef.participant.isLocal);
    const live = local && isTrackReference(local) && !local.publication.isMuted;
    return (
        <div className="flex h-[120px] items-center justify-center overflow-hidden rounded-xl bg-[var(--phq-call-canvas)] text-sm text-[var(--phq-call-ink-muted)] shadow-[inset_0_0_0_1px_var(--phq-call-line)]">
            {live && isTrackReference(local) ? (
                <VideoTrack trackRef={local} className="h-full w-full object-cover" style={{transform: 'scaleX(-1)'}}/>
            ) : (
                intl.formatMessage(m.cameraPreview)
            )}
        </div>
    );
};

export const PreviewPlaceholder = () => {
    const intl = useIntl();
    return (
        <div className="flex h-[120px] items-center justify-center rounded-xl bg-[var(--phq-call-canvas)] text-sm text-[var(--phq-call-ink-muted)] shadow-[inset_0_0_0_1px_var(--phq-call-line)]">
            {intl.formatMessage(m.cameraPreview)}
        </div>
    );
};

interface DevicesDialogProps {
    readonly microphoneBlocked?: boolean;
    readonly showCamera: boolean;
    readonly preview?: ReactNode;
    readonly onApply?: (kind: MediaDeviceKind, deviceId: string) => void;
    readonly current?: MediaDevicePreferences;
    readonly onClose: () => void;
}

export const DevicesDialog = ({showCamera, preview, onApply = groupActions.switchDevice, current, microphoneBlocked = false, onClose}: DevicesDialogProps) => {
    const intl = useIntl();
    const [saved] = useState<MediaDevicePreferences>(() => current ?? loadDevicePreferences());
    const [draft, setDraft] = useState<MediaDevicePreferences>(saved);
    const choose = (kind: MediaDeviceKind) => (deviceId: string) => setDraft(current => ({...current, [deviceKindKey(kind)]: deviceId || undefined}));
    const kinds: MediaDeviceKind[] = ['audioinput', ...(supportsAudioOutputSelection() ? ['audiooutput' as const] : []), ...(showCamera ? ['videoinput' as const] : [])];

    const save = () => {
        if (!current) {
            saveDevicePreferences(draft);
        }
        kinds.forEach(kind => {
            const key = deviceKindKey(kind);
            const deviceId = draft[key];
            if (deviceId && deviceId !== saved[key]) {
                onApply(kind, deviceId);
            }
        });
        onClose();
    };

    return (
        <CallDialog
            title={intl.formatMessage(m.devicesTitle)}
            closeLabel={intl.formatMessage(m.devicesClose)}
            testId="group-call-settings-dialog"
            onClose={onClose}
            footer={(
                <>
                    <DialogButton tone="neutral" label={intl.formatMessage(m.cancel)} onClick={onClose}/>
                    <DialogButton tone="brand" label={intl.formatMessage(m.save)} testId="group-call-devices-save" onClick={save}/>
                </>
            )}
        >
            <div className="flex flex-col gap-5">
                <DeviceSelect kind="audioinput" label={intl.formatMessage(gm.microphone)} value={draft.audioInputId} onChange={choose('audioinput')}/>
                {microphoneBlocked && <MicBlockedNote/>}
                {supportsAudioOutputSelection() && (
                    <DeviceSelect kind="audiooutput" label={intl.formatMessage(gm.speaker)} value={draft.audioOutputId} onChange={choose('audiooutput')}/>
                )}
                {showCamera && (
                    <>
                        <DeviceSelect kind="videoinput" label={intl.formatMessage(gm.camera)} value={draft.videoInputId} onChange={choose('videoinput')}/>
                        {preview ?? <PreviewPlaceholder/>}
                    </>
                )}
            </div>
        </CallDialog>
    );
};

/** Opens Privacy & Security → Screen Recording; the browser asks before handing off to System Settings. */
export const MAC_SCREEN_RECORDING_SETTINGS = 'x-apple.systempreferences:com.apple.preference.security?Privacy_ScreenCapture';

const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || navigator.userAgent);

export const ShareProblemCard = ({problem, onRetry, onDismiss}: {problem: 'denied' | 'failed'; onRetry: () => void; onDismiss: () => void}) => {
    const intl = useIntl();
    const denied = problem === 'denied';
    return (
        <div role="alert" className="phq-call-ui phq-call-swap fixed left-1/2 top-[60px] z-[75] flex w-[440px] max-w-[calc(100vw-32px)] -translate-x-1/2 gap-3 rounded-2xl bg-[var(--phq-call-surface)] p-4 shadow-[0_16px_48px_rgba(0,0,0,.6),inset_0_0_0_1px_var(--phq-call-line)]">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--phq-call-danger-tile)] text-[var(--phq-call-danger-soft)]">
                {denied ? <MonitorX size={20} aria-hidden="true"/> : <AlertCircle size={20} aria-hidden="true"/>}
            </span>
            <div className="flex min-w-0 flex-col gap-3">
                <div>
                    <p className="text-[15px] font-semibold">{intl.formatMessage(denied ? m.shareDeniedTitle : m.shareErrorTitle)}</p>
                    <p className="mt-1 text-sm leading-[1.45] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(denied ? m.shareDeniedBody : m.shareErrorBody)}</p>
                </div>
                <div className="flex gap-2">
                    {denied && isMac() ? (
                        // D-CONF-05/S04: on macOS the fix lives in System Settings.
                        <a
                            href={MAC_SCREEN_RECORDING_SETTINGS}
                            data-testid="share-open-system-settings"
                            className="phq-call-press inline-flex h-9 items-center rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white no-underline"
                        >
                            {intl.formatMessage(m.openSystemSettings)}
                        </a>
                    ) : (
                        <button type="button" onClick={onRetry} className="phq-call-press h-9 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white">
                            {intl.formatMessage(m.tryAgain)}
                        </button>
                    )}
                    <button type="button" onClick={onDismiss} className="phq-call-press h-9 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-sm font-semibold">
                        {intl.formatMessage(m.dismiss)}
                    </button>
                </div>
            </div>
        </div>
    );
};
